import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { toast } from "@shared/components/ui/toast";
import { getApiHeaders, getBaseUrl } from "@shared/config/api";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useRouter } from "vue-router";

export class ApiService {
  private static instance: ApiService;
  private readonly axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: getBaseUrl(),
      headers: getApiHeaders(),
      timeout: 10000,
    });

    this.setupInterceptors();
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  public async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  public async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  private ensureGuestId(): string {
    const memberId = localStorage.getItem("member_id") ?? sessionStorage.getItem("member_id");
    if (memberId) {
      return memberId;
    }

    const guestId = localStorage.getItem("guest_id") ?? sessionStorage.getItem("guest_id");
    const guestData = localStorage.getItem("guest_data") ?? sessionStorage.getItem("guest_data");

    if (guestId && guestData) {
      try {
        const { id, timestamp } = JSON.parse(guestData);
        const GUEST_EXPIRY_HOURS = 24;
        const isExpired = Date.now() - timestamp > GUEST_EXPIRY_HOURS * 60 * 60 * 1000;

        if (!isExpired && id) {
          return id;
        } else {
          localStorage.removeItem("guest_data");
          sessionStorage.removeItem("guest_data");
          localStorage.removeItem("guest_id");
          sessionStorage.removeItem("guest_id");
        }
      } catch (error) {
        console.warn("[API] Invalid guest data format, generating new guest ID", error);
        localStorage.removeItem("guest_data");
        sessionStorage.removeItem("guest_data");
        localStorage.removeItem("guest_id");
        sessionStorage.removeItem("guest_id");
      }
    }

    const newGuestId = `guest-${uuidv4()}`;
    const guestDataWithTimestamp = {
      id: newGuestId,
      timestamp: Date.now(),
    };

    localStorage.setItem("guest_data", JSON.stringify(guestDataWithTimestamp));
    localStorage.setItem("guest_id", newGuestId);

    return newGuestId;
  }

  private handleAuthError(): void {
    const router = useRouter();

    localStorage.removeItem("member_id");
    sessionStorage.removeItem("member_id");

    if (typeof window !== "undefined" && !window.location.pathname.includes("my-account")) {
      console.warn("[API] Authentication error - redirecting to login");
      router.push("/my-account");
    }
  }

  private isAuthError(error: unknown): boolean {
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as { response?: { status?: number } };
      return axiosError.response?.status === 401 || axiosError.response?.status === 403;
    }
    return false;
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const userId = this.ensureGuestId();
        config.headers["X-User-Id"] = userId;

        return config;
      },
      (error: unknown) => {
        console.error("[API] Request interceptor error:", error);
        return Promise.reject(this.toError(error));
      },
    );

    // Response interceptor for handling common errors
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: unknown) => {
        console.error("[API] Response error:", error);

        // 429 Too Many Requests
        if (error && typeof error === "object" && "response" in error) {
          const axiosError = error as { response?: { status?: number } };
          if (axiosError.response?.status === 429) {
            toast({
              description: "Too many requests, please try again later.",
              title: "Error",
              variant: "destructive",
            });
            return Promise.reject(this.toError(error));
          }
        }

        // Handle authentication errors
        if (this.isAuthError(error)) {
          this.handleAuthError();
        }

        return Promise.reject(this.toError(error));
      },
    );
  }

  // Helper to safely convert error to Error instance
  private toError(err: unknown): Error {
    if (err instanceof Error) return err;
    if (typeof err === "string") return new Error(err);
    if (err && typeof err === "object" && "message" in err) {
      return new Error(String((err as { message: unknown }).message));
    }
    return new Error("An unknown error occurred");
  }
}

export const api = ApiService.getInstance();
